import {
  createErrorHandler,
  FormValidation,
  RJSFSchema,
  UiSchema,
} from '@rjsf/utils'

/**
 * Adds custom validations for specific fields based on the uiSchema properties.
 * Needed to validate custom fields like RJSFQuantityField.
 * @param formData formData
 * @param errors FormValidation object to add errors toErrorSchema
 * @param uiSchema the uiSchema to read custom validation rules from
 * @returns
 */
export function WorkingStepFormValidator(
  formData: Record<string, any>,
  errors: FormValidation<any>,
  uiSchema: UiSchema<any, RJSFSchema, any>,
) {
  function validationNode(path: (string | number)[]): FormValidation<any> {
    let node = errors
    for (const segment of path) {
      const key = String(segment)
      node[key] ??= createErrorHandler<any>({})
      node = node[key]
    }
    return node
  }

  function visitNode(
    uiNode: Record<string, any>,
    dataNode: Record<string, any>,
    path: (string | number)[],
  ) {
    if (!uiNode || typeof uiNode !== 'object' || !dataNode) return

    const fieldType = uiNode['ui:field']

    switch (fieldType?.toLowerCase()) {
      // for custom fields, implements specific validations here
      case 'quantityfield': {
        if (
          uiNode['required'] &&
          (dataNode?.['value'] === null || dataNode?.['value'] === undefined)
        ) {
          validationNode(path).addError(`${uiNode['ui:label']} is required`)
        }

        if (!uiNode['required'] && dataNode?.['value'] == null) {
          return
        }

        if (uiNode['min-value'] !== undefined) {
          if (dataNode?.['value'] < uiNode['min-value']) {
            validationNode(path).addError(
              `Value must be greater than or equal to ${uiNode['min-value']}`,
            )
          }
        }

        if (uiNode['max-value'] !== undefined) {
          if (dataNode['value'] > uiNode['max-value']) {
            validationNode(path).addError(
              `Value must be less than or equal to ${uiNode['max-value']}`,
            )
          }
        }
        break
      }
    }

    // if this field is an array, apply the uiSchema for each item
    if (Array.isArray(dataNode) && uiNode.items) {
      for (let i = 0; i < dataNode.length; i++) {
        visitNode(uiNode.items, dataNode[i], [...path, i])
      }
    }

    // Recurse into object children (ignore ui: and items)
    for (const key of Object.keys(uiNode)) {
      if (key.startsWith('ui:') || key === 'items') continue
      visitNode(uiNode[key], dataNode?.[key], [...path, key])
    }
  }

  const _customValidate = () => {
    visitNode(uiSchema, formData, [])
    return errors
  }

  return _customValidate()
}
